import { Path, setByPath } from 'dot-path-value';
import React, { useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { SvgIconUrl } from 'common/components';

import svgIcons from 'common/svgIcons';

import { routes } from 'routes/routes.constants';

import './ThemeProvider.css';
import { Themes } from './types';
import { DEFAULT_THEME_LAYOUT } from './constants';
import { ThemeContext, ThemeLayoutType } from './Theme.context';

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const defaultTheme = useMemo(() => Themes.LIGHT, []);

  const storageTheme = useMemo(() => (localStorage.getItem('webtrader:theme') as Themes) || '', []);

  const [layout, updateLayout] = useLocalStorage<ThemeLayoutType>(
    'webtrader:storage:theme:layout',
    DEFAULT_THEME_LAYOUT,
  );

  const isTradeRoute = useRouteMatch([
    routes.getTradePage('spot'),
    routes.getTradePage('futures'),
    routes.getTradePage('stocks'),
  ]);

  const onUpdateLayout = useCallback(
    (key: Path<ThemeLayoutType>, value: boolean) => {
      const update = setByPath(layout, key, value);
      updateLayout(update);
    },
    [layout, updateLayout],
  );

  const userTheme = useMemo(() => Object.values(Themes).includes(storageTheme) && storageTheme, [storageTheme]);

  const [theme, setTheme] = useState<Themes>(userTheme || defaultTheme);

  useEffect(() => {
    document.documentElement.className = theme;
  }, [theme]);

  const changeTheme = useCallback((themeName: Themes) => {
    setTheme(themeName);
    localStorage.setItem('webtrader:theme', themeName);
  }, []);

  const getLayoutConfig = useCallback(
    (): ThemeLayoutType => ({
      ...layout,
      sidebar: {
        compact: layout.sidebar.compact === undefined ? !!isTradeRoute : layout.sidebar.compact,
      },
    }),
    [isTradeRoute, layout],
  );

  const value = useMemo(
    () => ({
      theme,
      changeTheme,
      layout: getLayoutConfig(),
      updateLayout: onUpdateLayout,
    }),
    [changeTheme, getLayoutConfig, onUpdateLayout, theme],
  );

  return (
    <ThemeContext.Provider value={value}>
      <div className='container'>{children}</div>
    </ThemeContext.Provider>
  );
};

export const ThemeSwitcher = () => {
  const { theme, changeTheme } = useContext(ThemeContext);

  const onThemeChange = useCallback(() => {
    if (theme === Themes.LIGHT) {
      changeTheme(Themes.DARK);
    } else {
      changeTheme(Themes.LIGHT);
    }
  }, [changeTheme, theme]);

  return (
    <div data-test={`theme-switcher-${theme}`} className='theme__btn' onClick={onThemeChange}>
      <div className='theme__switcher'>{theme === Themes.DARK && <SvgIconUrl symbol={svgIcons.ThemeDark()} />}</div>
    </div>
  );
};
